/**
=========================================================
* Material Dashboard 3 PRO React - v2.4.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2024 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import axios from "axios";
import swal from "sweetalert";
import jsPDF from "jspdf";
import { applyPlugin } from "jspdf-autotable";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import CircularProgress from "@mui/material/CircularProgress";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import Autocomplete from "@mui/material/Autocomplete";

// Material Dashboard 3 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDEditor from "components/MDEditor";
import MDDropzone from "components/MDDropzone";
import MDProgress from "components/MDProgress";

// Material Dashboard 3 PRO React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";

// Icons
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";
import PersonIcon from "@mui/icons-material/Person";
import AssessmentIcon from "@mui/icons-material/Assessment";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PeopleIcon from "@mui/icons-material/People";
import CancelIcon from "@mui/icons-material/Cancel";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import PhotoLibraryIcon from "@mui/icons-material/PhotoLibrary";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import LinkIcon from "@mui/icons-material/Link";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import PlaceIcon from "@mui/icons-material/Place";
import BusinessIcon from "@mui/icons-material/Business";

import { QRCodeSVG } from "qrcode.react";

import { logoutUser } from "actions/AuthAction";
import { useNavigate } from "react-router-dom";
import { API_ESA_OFFICE, API_ESA_OFFICE_UPLOAD } from "utils";

applyPlugin(jsPDF);

// Helper untuk format datetime
function formatDateTime(dateStr) {
  if (!dateStr) return "-";
  const date = new Date(dateStr);
  if (isNaN(date)) return "-";
  return date.toLocaleString("id-ID", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// Helper format date
function formatDate(dateStr) {
  if (!dateStr) return "-";
  const d = new Date(dateStr);
  if (isNaN(d)) return "-";
  return d.toLocaleDateString("id-ID", { day: "numeric", month: "short", year: "numeric" });
}

// Helper untuk memotong nama dinas menjadi 3 kata
function truncateDinasName(nama) {
  if (!nama) return "";
  const words = nama.trim().split(/\s+/);
  if (words.length <= 3) return nama;
  return words.slice(0, 3).join(" ") + "...";
}

// Helper load logo Rote Ndao untuk PDF
async function loadLogoBase64() {
  try {
    const logoUrl = `${process.env.PUBLIC_URL || ""}/logorote.png`;
    const res = await fetch(logoUrl, { credentials: "omit" });
    if (!res.ok) return null;
    const blob = await res.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

// Helper tambah logo di pojok kanan atas PDF
function addLogoToPdf(doc, logoBase64) {
  if (!logoBase64) return;
  try {
    const pageW = doc.internal.pageSize.getWidth();
    const logoW = 12;
    const logoH = 12;
    const x = pageW - logoW - 14;
    const y = 10;
    doc.addImage(logoBase64, "PNG", x, y, logoW, logoH);
  } catch (e) {
    console.warn("Logo add failed:", e);
  }
}

function LaporanEvent() {
  const [detailEventsList, setDetailEventsList] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [openEditDetailDialog, setOpenEditDetailDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingDetailEvent, setEditingDetailEvent] = useState(null);
  const [dinasId, setDinasId] = useState(null);
  const [userNamaDinas, setUserNamaDinas] = useState("");
  const [dinasList, setDinasList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submittingDetail, setSubmittingDetail] = useState(false);
  const [tahun, setTahun] = useState(new Date().getFullYear()); // default: tahun berjalan
  const [bulan, setBulan] = useState(null); // default: semua bulan
  const [searchQuery, setSearchQuery] = useState("");
  const [eventStatistics, setEventStatistics] = useState({}); // { eventId: statistics }
  const navigate = useNavigate();
  const dropzoneRef = useRef(null);

  const [formData, setFormData] = useState({
    notulen: "",
    foto: [],
  });

  const [detailFormData, setDetailFormData] = useState({
    detail_kegiatan: "",
    waktu_mulai: "",
    waktu_finish: "",
    tempat: "",
    dinas_id: null,
  });

  // State untuk target dinas di dialog edit
  const [targetDinasForEdit, setTargetDinasForEdit] = useState([]);

  // State untuk foto existing dan yang akan dihapus
  const [existingFotos, setExistingFotos] = useState([]);
  const [removedFotoUrls, setRemovedFotoUrls] = useState([]);

  // State untuk dialog view notulen/foto
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [viewingDetailEvent, setViewingDetailEvent] = useState(null);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);

  // State untuk dialog progress MD
  const [openProgressDialog, setOpenProgressDialog] = useState(false);
  const [viewingProgressMD, setViewingProgressMD] = useState("");

  // State untuk dialog daftar peserta
  const [openPesertaDialog, setOpenPesertaDialog] = useState(false);
  const [daftarPeserta, setDaftarPeserta] = useState([]);
  const [loadingPeserta, setLoadingPeserta] = useState(false);
  const [selectedDetailEventId, setSelectedDetailEventId] = useState(null);

  // State untuk dialog dinas tanpa event
  const [openDinasTanpaEventDialog, setOpenDinasTanpaEventDialog] =
    useState(false);
  const [dinasTanpaEvent, setDinasTanpaEvent] = useState([]);
  const [loadingDinasTanpaEvent, setLoadingDinasTanpaEvent] = useState(false);
  const [selectedEventIdForDinas, setSelectedEventIdForDinas] = useState(null);

  // State untuk dialog dinas hadir/belum hadir
  const [openDinasHadirDialog, setOpenDinasHadirDialog] = useState(false);
  const [dinasHadirList, setDinasHadirList] = useState([]);
  const [loadingDinasHadir, setLoadingDinasHadir] = useState(false);
  const [selectedEventIdForDinasHadir, setSelectedEventIdForDinasHadir] =
    useState(null);
  const [eventDataForDinasHadirDialog, setEventDataForDinasHadirDialog] =
    useState(null);

  // State untuk dialog dinas sudah selesai
  const [openDinasSelesaiDialog, setOpenDinasSelesaiDialog] = useState(false);
  const [dinasSelesaiList, setDinasSelesaiList] = useState([]);
  const [loadingDinasSelesai, setLoadingDinasSelesai] = useState(false);
  const [selectedEventIdForDinasSelesai, setSelectedEventIdForDinasSelesai] =
    useState(null);
  const [eventDataForDialog, setEventDataForDialog] = useState(null);

  // State untuk dialog foto kegiatan
  const [openFotoDialog, setOpenFotoDialog] = useState(false);
  const [viewingFotos, setViewingFotos] = useState([]);

  // State untuk dialog QR Code daftar hadir
  const [openQrDialog, setOpenQrDialog] = useState(false);
  const [qrDialogEvent, setQrDialogEvent] = useState(null);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (user) {
      setDinasId(user.dinas_id);
      setUserNamaDinas(user.nama_dinas || "");

      // Fetch dinas list untuk Autocomplete
      axios
        .get(`${API_ESA_OFFICE}dinas`)
        .then((res) => {
          if (res.data && Array.isArray(res.data)) {
            setDinasList(res.data.map((d) => d.nama_dinas || d.nama));
          }
        })
        .catch((err) => {
          console.error("Error fetching dinas list:", err);
        });
    } else {
      logoutUser(navigate);
    }
  }, []);

  const fetchDetailEvents = async () => {
    if (!dinasId) return;

    setLoading(true);
    try {
      let url = `${API_ESA_OFFICE}events/detail/all?dinasId=${dinasId}`;
      if (tahun) {
        url += `&tahun=${tahun}`;
      }
      if (bulan) {
        url += `&bulan=${bulan}`;
      }
      const response = await axios.get(url);
      const data = response.data || [];
      // Pastikan id_pembuat selalu string untuk konsistensi
      // Pastikan id_events ada di setiap item
      const formattedData = data.map((item) => ({
        ...item,
        id_pembuat: item.id_pembuat ? String(item.id_pembuat) : null,
        id_events: item.id_events || null,
        dinas_peserta_nama: item.dinas_peserta_nama || null,
        event_nama_pembuat: item.event_nama_pembuat || null,
        event_uid_RSS_pembuat: item.event_uid_RSS_pembuat || null,
        event_id_pembuat: item.event_id_pembuat || null,
      }));
      console.log(
        "fetchDetailEvents - formattedData sample:",
        formattedData.length > 0 ? formattedData[0] : "No data"
      );
      setDetailEventsList(formattedData);
    } catch (error) {
      console.error("Error fetching detail events:", error);
      swal({
        title: "Error",
        text: "Gagal mengambil data detail events",
        icon: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (dinasId) {
      fetchDetailEvents();
    }
  }, [dinasId, tahun, bulan]);

  const handleOpenDialog = (detailEvent) => {
    setEditingId(detailEvent.id);
    setFormData({
      notulen: detailEvent.notulen || "",
      foto: [],
    });
    // Set foto existing
    const fotos =
      detailEvent.foto && Array.isArray(detailEvent.foto)
        ? detailEvent.foto
        : [];
    setExistingFotos(fotos);
    setRemovedFotoUrls([]);
    if (dropzoneRef.current && dropzoneRef.current.dropzone) {
      dropzoneRef.current.dropzone.removeAllFiles(true);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingId(null);
    setFormData({
      notulen: "",
      foto: [],
    });
    setExistingFotos([]);
    setRemovedFotoUrls([]);
    if (dropzoneRef.current && dropzoneRef.current.dropzone) {
      dropzoneRef.current.dropzone.removeAllFiles(true);
    }
    setSubmitting(false);
  };

  const handleRemoveExistingFoto = (fotoUrl) => {
    setExistingFotos((prev) => prev.filter((url) => url !== fotoUrl));
    setRemovedFotoUrls((prev) => [...prev, fotoUrl]);
  };

  const generatePDF = async (detailEvent) => {
    // Fetch data peserta
    let pesertaList = [];
    try {
      const pesertaResponse = await axios.get(
        `${API_ESA_OFFICE}attendance/${detailEvent.id}`
      );
      pesertaList = pesertaResponse.data || [];
    } catch (error) {
      console.error("Error fetching peserta:", error);
    }
    const baseUrl = API_ESA_OFFICE_UPLOAD.replace(/\/$/, "");
    const signatureBase64Map = {};
    await Promise.all(
      (pesertaList || []).map(async (p, idx) => {
        const sig = p.signature_file;
        if (!sig || typeof sig !== "string") return;
        const imgUrl = `${baseUrl}/uploads/${sig.startsWith("/") ? sig.slice(1) : sig}`;
        try {
          const res = await fetch(imgUrl, { credentials: "include" });
          if (!res.ok) return;
          const blob = await res.blob();
          const base64 = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          });
          signatureBase64Map[idx] = base64;
        } catch {
          // skip jika gagal load gambar
        }
      })
    );
    const doc = new jsPDF();
    const logoBase64 = await loadLogoBase64();
    addLogoToPdf(doc, logoBase64);
    let yPosition = 20;

    // Helper function untuk render HTML dengan mempertahankan formatting
    const renderHtmlContent = (html, x, y, maxWidth) => {
      if (!html) return y;

      let currentY = y;
      const lineHeight = 5;
      const paragraphSpacing = 3;

      // Set default font normal
      doc.setFont("helvetica", "normal");
      doc.setFontSize(11);

      // Parse HTML
      const tmp = document.createElement("DIV");
      tmp.innerHTML = html;

      // Track current font state
      let currentFontStyle = "normal"; // "normal", "bold", "italic"

      // Process nodes recursively
      const processNode = (node, indent = 0, parentFontStyle = "normal") => {
        if (node.nodeType === Node.TEXT_NODE) {
          const text = node.textContent.trim();
          if (text) {
            // Check if need new page
            if (currentY > 270) {
              doc.addPage();
              currentY = 20;
            }

            // Get computed style untuk text alignment
            const parent = node.parentElement;
            const textAlign =
              parent?.style?.textAlign ||
              window.getComputedStyle(parent || document.body).textAlign ||
              "left";

            // Set font sesuai parent font style
            if (parentFontStyle === "bold") {
              doc.setFont("helvetica", "bold");
            } else if (parentFontStyle === "italic") {
              doc.setFont("helvetica", "italic");
            } else {
              doc.setFont("helvetica", "normal");
            }

            // Split text to fit width
            const lines = doc.splitTextToSize(text, maxWidth - indent * 5);

            // Render each line dengan alignment
            lines.forEach((line) => {
              if (currentY > 270) {
                doc.addPage();
                currentY = 20;
              }

              let xPos = x + indent * 5;
              if (textAlign === "center") {
                xPos = 105; // Center of page
                doc.text(line, xPos, currentY, { align: "center" });
              } else if (textAlign === "right") {
                xPos = x + maxWidth;
                doc.text(line, xPos, currentY, { align: "right" });
              } else {
                doc.text(line, xPos, currentY);
              }
              currentY += lineHeight;
            });
          }
        } else if (node.nodeType === Node.ELEMENT_NODE) {
          const tagName = node.tagName?.toLowerCase();

          // Handle different HTML elements
          if (tagName === "p" || tagName === "div") {
            // Add spacing before paragraph (except first)
            if (currentY !== y && node.previousSibling) {
              currentY += paragraphSpacing;
            }

            // Process children dengan font style dari parent
            Array.from(node.childNodes).forEach((child) => {
              processNode(child, indent, parentFontStyle);
            });

            // Add spacing after paragraph
            currentY += paragraphSpacing;
          } else if (tagName === "br") {
            currentY += lineHeight;
          } else if (tagName === "strong" || tagName === "b") {
            // Bold text - pass "bold" to children
            Array.from(node.childNodes).forEach((child) => {
              processNode(child, indent, "bold");
            });
          } else if (tagName === "em" || tagName === "i") {
            // Italic text - pass "italic" to children
            Array.from(node.childNodes).forEach((child) => {
              processNode(child, indent, "italic");
            });
          } else if (tagName === "ul" || tagName === "ol") {
            // List
            Array.from(node.children).forEach((child, index) => {
              if (currentY > 270) {
                doc.addPage();
                currentY = 20;
              }

              // Set font normal untuk bullet
              doc.setFont("helvetica", "normal");
              const bullet = tagName === "ul" ? "•" : `${index + 1}.`;
              doc.text(bullet, x + indent * 5, currentY);
              processNode(child, indent + 1, parentFontStyle);
            });
          } else if (tagName === "li") {
            // List item
            Array.from(node.childNodes).forEach((child) => {
              processNode(child, indent, parentFontStyle);
            });
          } else if (tagName === "h1" || tagName === "h2" || tagName === "h3") {
            // Headings - always bold
            if (currentY !== y && node.previousSibling) {
              currentY += paragraphSpacing * 2;
            }
            const fontSize = tagName === "h1" ? 16 : tagName === "h2" ? 14 : 12;
            doc.setFontSize(fontSize);
            doc.setFont("helvetica", "bold");
            Array.from(node.childNodes).forEach((child) => {
              processNode(child, indent, "bold");
            });
            doc.setFontSize(11);
            doc.setFont("helvetica", "normal");
            currentY += paragraphSpacing;
          } else {
            // Process other elements - maintain parent font style
            Array.from(node.childNodes).forEach((child) => {
              processNode(child, indent, parentFontStyle);
            });
          }
        }
      };

      // Process all nodes dengan font normal sebagai default
      Array.from(tmp.childNodes).forEach((child) => {
        processNode(child, 0, "normal");
      });

      // Reset font ke normal setelah selesai
      doc.setFont("helvetica", "normal");
      doc.setFontSize(11);

      return currentY;
    };

    // Header
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text("Laporan Event", 105, yPosition, { align: "center" });
    yPosition += 10;

    // Informasi Event
    doc.setFontSize(12);
    doc.setFont("helvetica", "normal");
    doc.text(`Nama Event: ${detailEvent.kegiatan || "-"}`, 14, yPosition);
    yPosition += 7;
    doc.text(
      `Detail Kegiatan: ${detailEvent.detail_kegiatan || "-"}`,
      14,
      yPosition
    );
    yPosition += 7;
    doc.text(
      `Lokasi Pelaksanaan Rapat: ${detailEvent.tempat || "-"}`,
      14,
      yPosition
    );
    yPosition += 7;

    const waktuMulai = detailEvent.waktu_mulai
      ? new Date(detailEvent.waktu_mulai).toLocaleString("id-ID")
      : "-";
    const waktuFinish = detailEvent.waktu_finish
      ? new Date(detailEvent.waktu_finish).toLocaleString("id-ID")
      : "-";
    doc.text(`Waktu Mulai: ${waktuMulai}`, 14, yPosition);
    yPosition += 7;
    doc.text(`Waktu Selesai: ${waktuFinish}`, 14, yPosition);
    yPosition += 10;

    // Notulen dengan HTML formatting
    if (detailEvent.notulen && detailEvent.notulen.trim() !== "") {
      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.text("Notulen", 14, yPosition);
      yPosition += 10;

      // Check if need new page
      if (yPosition > 250) {
        doc.addPage();
        yPosition = 20;
      }

      // Render HTML content dengan mempertahankan formatting
      yPosition = renderHtmlContent(detailEvent.notulen, 14, yPosition, 180);
    }

    // Foto
    if (
      detailEvent.foto &&
      Array.isArray(detailEvent.foto) &&
      detailEvent.foto.length > 0
    ) {
      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.text(`Foto Kegiatan (${detailEvent.foto.length})`, 14, yPosition);
      yPosition += 10;

      for (let i = 0; i < detailEvent.foto.length; i++) {
        const fotoUrl = detailEvent.foto[i];
        const fullUrl = fotoUrl.startsWith("/")
          ? `${API_ESA_OFFICE_UPLOAD}${fotoUrl}`
          : `${API_ESA_OFFICE_UPLOAD}/${fotoUrl}`;

        try {
          // Load image
          const img = await new Promise((resolve, reject) => {
            const image = new Image();
            image.crossOrigin = "anonymous";
            image.onload = () => resolve(image);
            image.onerror = reject;
            image.src = fullUrl;
          });

          // Calculate image dimensions to fit page width
          const maxWidth = 180;
          const maxHeight = 100;
          let imgWidth = img.width;
          let imgHeight = img.height;
          const ratio = Math.min(maxWidth / imgWidth, maxHeight / imgHeight);
          imgWidth = imgWidth * ratio;
          imgHeight = imgHeight * ratio;

          // Check if need new page
          if (yPosition + imgHeight > 270) {
            doc.addPage();
            yPosition = 20;
          }

          // Add image
          doc.text(`Foto ${i + 1}:`, 14, yPosition);
          yPosition += 5;
          doc.addImage(img, "JPEG", 14, yPosition, imgWidth, imgHeight);
          yPosition += imgHeight + 10;

          // Check if need new page after image
          if (yPosition > 250 && i < detailEvent.foto.length - 1) {
            doc.addPage();
            yPosition = 20;
          }
        } catch (error) {
          console.error("Error loading image:", error);
          doc.text(`Foto ${i + 1}: Gagal memuat gambar`, 14, yPosition);
          yPosition += 7;
        }
      }
    }

    // Daftar Peserta (dengan kolom Tanda Tangan dan gambar, sama seperti dialog peserta)
    if (pesertaList.length > 0) {
      // Check if need new page
      if (yPosition > 220) {
        doc.addPage();
        yPosition = 20;
      }

      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.text(`Daftar Peserta (${pesertaList.length} orang)`, 14, yPosition);
      yPosition += 10;

      const sigColIdx = 4;
      const sigColWidthMm = 42;
      const padding = 4;
      const headers = [["No", "Nama", "Instansi", "Waktu", "Tanda Tangan"]];
      const body = pesertaList.map((p, idx) => [
        String(idx + 1),
        (p.nama_user || p.nama || "-")
          .replace(/\r\n/g, " ")
          .replace(/\n/g, " ")
          .substring(0, 50),
        (p.instansi || "-").replace(/\r\n/g, " ").replace(/\n/g, " "),
        p.waktu ? formatDateTime(p.waktu) : "-",
        signatureBase64Map[idx] ? "" : "-",
      ]);

      doc.autoTable({
        head: headers,
        body,
        startY: yPosition,
        styles: { fontSize: 9 },
        headStyles: { fillColor: [41, 128, 185], fontSize: 9 },
        alternateRowStyles: { fillColor: [248, 249, 250] },
        margin: { left: 14, right: 14 },
        showHead: "everyPage",
        minCellHeight: 20,
        columnStyles: {
          0: { cellWidth: 10 },
          1: { cellWidth: 50 },
          2: { cellWidth: 36 },
          3: { cellWidth: 34 },
          4: { cellWidth: sigColWidthMm },
        },
        didDrawCell: (data) => {
          if (data.section !== "body" || data.column.index !== sigColIdx) return;
          if (!signatureBase64Map[data.row.index]) return;
          const cell = data.cell;
          const base64 = signatureBase64Map[data.row.index];
          const imgW = Math.max(8, Math.min(cell.width - padding * 2, 36));
          const imgH = Math.max(6, Math.min(cell.height - padding * 2, 14));
          const x = cell.x + padding;
          const y = cell.y + padding;
          try {
            doc.addImage(base64, "PNG", x, y, imgW, imgH);
          } catch {
            // fallback jika addImage gagal
          }
        },
      });
      yPosition = doc.lastAutoTable.finalY + 5;
    }

    // Generate PDF blob
    const pdfBlob = doc.output("blob");
    const url = URL.createObjectURL(pdfBlob);
    return url;
  };

  const handleOpenViewDialog = useCallback(async (detailEvent) => {
    setViewingDetailEvent(detailEvent);
    setOpenViewDialog(true);

    // Generate PDF
    try {
      const pdfUrl = await generatePDF(detailEvent);
      setPdfBlobUrl(pdfUrl);
    } catch (error) {
      console.error("Error generating PDF:", error);
      swal({
        title: "Error",
        text: "Gagal membuat PDF",
        icon: "error",
      });
    }
  }, []);

  const handleCloseViewDialog = () => {
    // Clean up blob URL
    if (pdfBlobUrl) {
      URL.revokeObjectURL(pdfBlobUrl);
      setPdfBlobUrl(null);
    }
    setOpenViewDialog(false);
    setViewingDetailEvent(null);
  };

  const handleOpenPesertaDialog = useCallback(
    async (detailEventId, jumlahPeserta) => {
      if (jumlahPeserta === 0) return;

      setSelectedDetailEventId(detailEventId);
      setOpenPesertaDialog(true);
      setLoadingPeserta(true);
      setDaftarPeserta([]);

      try {
        const response = await axios.get(
          `${API_ESA_OFFICE}attendance/${detailEventId}`
        );
        setDaftarPeserta(response.data || []);
      } catch (error) {
        console.error("Error fetching daftar peserta:", error);
        swal({
          title: "Error",
          text: "Gagal mengambil data peserta",
          icon: "error",
        });
        setDaftarPeserta([]);
      } finally {
        setLoadingPeserta(false);
      }
    },
    []
  );

  const handleClosePesertaDialog = () => {
    setOpenPesertaDialog(false);
    setDaftarPeserta([]);
    setSelectedDetailEventId(null);
  };

  const [downloadingPesertaPdf, setDownloadingPesertaPdf] = useState(false);

  const handleDownloadPesertaPDF = useCallback(async () => {
    if (!daftarPeserta?.length) return;

    setDownloadingPesertaPdf(true);
    try {
      const baseUrl = API_ESA_OFFICE_UPLOAD.replace(/\/$/, "");
      const signatureBase64Map = {};
      await Promise.all(
        daftarPeserta.map(async (p, idx) => {
          const sig = p.signature_file;
          if (!sig || typeof sig !== "string") return;
          const imgUrl = `${baseUrl}/uploads/${sig.startsWith("/") ? sig.slice(1) : sig}`;
          try {
            const res = await fetch(imgUrl, { credentials: "include" });
            if (!res.ok) return;
            const blob = await res.blob();
            const base64 = await new Promise((resolve, reject) => {
              const reader = new FileReader();
              reader.onloadend = () => resolve(reader.result);
              reader.onerror = reject;
              reader.readAsDataURL(blob);
            });
            signatureBase64Map[idx] = base64;
          } catch {
            // skip jika gagal load gambar
          }
        })
      );

      const detailEv = detailEventsList.find((d) => d.id === selectedDetailEventId);
      const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: [215, 279] });
      const logoBase64 = await loadLogoBase64();
      addLogoToPdf(doc, logoBase64);
      let yPos = 12;

      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.text("Daftar Peserta Hadir", 14, yPos);
      yPos += 6;

      doc.setFontSize(9);
      doc.setFont("helvetica", "normal");
      doc.text(`Detail Kegiatan: ${detailEv?.detail_kegiatan || "-"}`, 14, yPos);
      yPos += 5;
      const tanggalPelaksanaan = detailEv?.waktu_mulai ? new Date(detailEv.waktu_mulai).toLocaleDateString("id-ID", { day: "numeric", month: "long", year: "numeric" }) : "-";
      doc.text(`Tanggal Pelaksanaan: ${tanggalPelaksanaan}`, 14, yPos);
      yPos += 5;
      doc.text(
        `Lokasi Pelaksanaan Rapat: ${detailEv?.tempat || "-"}`,
        14,
        yPos
      );
      yPos += 5;
      doc.text(`Jumlah Peserta Hadir: ${daftarPeserta.length} orang`, 14, yPos);
      yPos += 8;

      const headers = [["No", "Nama", "Instansi", "Waktu", "Tanda Tangan"]];
      const body = daftarPeserta.map((p, idx) => [
        String(idx + 1),
        (p.nama_user || p.nama || "").replace(/\r\n/g, " ").replace(/\n/g, " "),
        (p.instansi || "-").replace(/\r\n/g, " ").replace(/\n/g, " "),
        p.waktu ? formatDateTime(p.waktu) : "-",
        signatureBase64Map[idx] ? "" : "-",
      ]);

      const sigColIdx = 4;
      const sigColWidthMm = 42;
      const padding = 4;

      doc.autoTable({
        head: headers,
        body,
        startY: yPos,
        styles: { fontSize: 8 },
        headStyles: { fillColor: [41, 128, 185], fontSize: 8 },
        alternateRowStyles: { fillColor: [248, 249, 250] },
        margin: { left: 14, right: 14 },
        showHead: "everyPage",
        minCellHeight: 20,
        columnStyles: {
          0: { cellWidth: 10 },
          1: { cellWidth: 50 },
          2: { cellWidth: 38 },
          3: { cellWidth: 35 },
          4: { cellWidth: sigColWidthMm },
        },
        didDrawCell: (data) => {
          if (data.section !== "body" || data.column.index !== sigColIdx) return;
          if (!signatureBase64Map[data.row.index]) return;
          const cell = data.cell;
          const base64 = signatureBase64Map[data.row.index];
          const imgW = Math.max(8, Math.min(cell.width - padding * 2, 32));
          const imgH = Math.max(6, Math.min(cell.height - padding * 2, 14));
          const x = cell.x + padding;
          const y = cell.y + padding;
          try {
            doc.addImage(base64, "PNG", x, y, imgW, imgH);
          } catch {
            // fallback jika addImage gagal
          }
        },
      });

      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const now = new Date();
      const tanggalDownload = now.toLocaleDateString("id-ID", { day: "numeric", month: "long", year: "numeric" });
      const jamDownload = now.toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
      doc.setFontSize(7);
      doc.setFont("helvetica", "normal");
      doc.text(
        `Didownload dari Esa Office Apps tanggal ${tanggalDownload} dan jam ${jamDownload} oleh ${user?.nama || user?.name || "User"}`,
        14,
        doc.internal.pageSize.getHeight() - 10
      );

      const safeTitle = (detailEv?.detail_kegiatan || "daftar-peserta").replace(/[^a-zA-Z0-9-_]/g, "_").substring(0, 50);
      doc.save(`daftar-peserta-${safeTitle}-${selectedDetailEventId}.pdf`);
    } catch (err) {
      console.error("PDF export error:", err);
      swal("Error", "Gagal export PDF: " + (err?.message || "Unknown error"), "error");
    } finally {
      setDownloadingPesertaPdf(false);
    }
  }, [daftarPeserta, selectedDetailEventId, detailEventsList]);

  const handleOpenDinasTanpaEventDialog = useCallback(async (eventId) => {
    setSelectedEventIdForDinas(eventId);
    setOpenDinasTanpaEventDialog(true);
    setLoadingDinasTanpaEvent(true);
    setDinasTanpaEvent([]);

    try {
      // Fetch target dinas
      const targetDinasResponse = await axios.get(
        `${API_ESA_OFFICE}events/${eventId}/target-dinas`
      );
      const targetDinas = targetDinasResponse.data || [];

      // Fetch detail events untuk mendapatkan dinas yang sudah punya detail event
      const detailEventsResponse = await axios.get(
        `${API_ESA_OFFICE}events/${eventId}/detail`
      );
      const detailEvents = detailEventsResponse.data || [];
      const dinasDenganEvent = detailEvents
        .map((de) => de.dinas_id)
        .filter((id) => id !== null && id !== undefined);

      // Filter dinas tanpa event (target dinas yang tidak ada di detail events)
      const dinasTanpaEventList = targetDinas.filter(
        (td) => !dinasDenganEvent.includes(td.dinas_id)
      );

      setDinasTanpaEvent(dinasTanpaEventList);
    } catch (error) {
      console.error("Error fetching dinas tanpa event:", error);
      swal({
        title: "Error",
        text: "Gagal mengambil data dinas tanpa event",
        icon: "error",
      });
      setDinasTanpaEvent([]);
    } finally {
      setLoadingDinasTanpaEvent(false);
    }
  }, []);

  const handleCloseDinasTanpaEventDialog = () => {
    setOpenDinasTanpaEventDialog(false);
    setDinasTanpaEvent([]);
    setSelectedEventIdForDinas(null);
  };

  const handleOpenDinasHadirDialog = useCallback(async (eventId, eventData) => {
    setSelectedEventIdForDinasHadir(eventId);
    setEventDataForDinasHadirDialog(eventData);
    setOpenDinasHadirDialog(true);
    setLoadingDinasHadir(true);
    setDinasHadirList([]);

    try {
      // Fetch target dinas (semua dinas target)
      const targetDinasResponse = await axios.get(
        `${API_ESA_OFFICE}events/${eventId}/target-dinas`
      );
      const targetDinas = targetDinasResponse.data || [];

      // Fetch statistics untuk mendapatkan dinas hadir (yang detail event-nya memiliki peserta)
      const statisticsResponse = await axios.get(
        `${API_ESA_OFFICE}events/${eventId}/statistics`
      );
      const statistics = statisticsResponse.data || {};
      const dinasSudahHadirIds = (statistics.dinas_sudah_hadir || []).map(
        (d) => d.dinas_id
      );

      // Fetch detail events untuk mendapatkan dinas yang sudah punya detail event
      const detailEventsResponse = await axios.get(
        `${API_ESA_OFFICE}events/${eventId}/detail`
      );
      const detailEvents = detailEventsResponse.data || [];
      const dinasDenganEventIds = detailEvents
        .map((de) => de.dinas_id)
        .filter((id) => id !== null && id !== undefined);

      // Klasifikasi semua target dinas
      const allDinas = targetDinas.map((td) => {
        // Jika dinas sudah hadir (detail event memiliki peserta)
        if (dinasSudahHadirIds.includes(td.dinas_id)) {
          return { ...td, status: "hadir" };
        }
        // Jika dinas belum punya detail event, dianggap belum hadir
        if (!dinasDenganEventIds.includes(td.dinas_id)) {
          return { ...td, status: "belum_hadir" };
        }
        // Jika dinas sudah punya detail event tapi belum ada peserta, dianggap belum hadir
        return { ...td, status: "belum_hadir" };
      });

      // Urutkan: hadir di atas, belum hadir di bawah
      const sortedDinas = allDinas.sort((a, b) => {
        if (a.status === "hadir" && b.status === "belum_hadir") return -1;
        if (a.status === "belum_hadir" && b.status === "hadir") return 1;
        return 0;
      });

      setDinasHadirList(sortedDinas);
    } catch (error) {
      console.error("Error fetching dinas hadir:", error);
      swal({
        title: "Error",
        text: "Gagal mengambil data dinas hadir",
        icon: "error",
      });
      setDinasHadirList([]);
    } finally {
      setLoadingDinasHadir(false);
    }
  }, []);

  const handleCloseDinasHadirDialog = () => {
    setOpenDinasHadirDialog(false);
    setDinasHadirList([]);
    setSelectedEventIdForDinasHadir(null);
    setEventDataForDinasHadirDialog(null);
  };

  const handleOpenDinasSelesaiDialog = useCallback(
    async (eventId, eventData) => {
      setSelectedEventIdForDinasSelesai(eventId);
      setEventDataForDialog(eventData);
      setOpenDinasSelesaiDialog(true);
      setLoadingDinasSelesai(true);
      setDinasSelesaiList([]);

      try {
        // Fetch target dinas
        const targetDinasResponse = await axios.get(
          `${API_ESA_OFFICE}events/${eventId}/target-dinas`
        );
        const targetDinas = targetDinasResponse.data || [];

        // Fetch detail events untuk mendapatkan dinas yang sudah selesai
        const detailEventsResponse = await axios.get(
          `${API_ESA_OFFICE}events/${eventId}/detail`
        );
        const detailEvents = detailEventsResponse.data || [];

        // Dapatkan dinas yang sudah selesai (status === "selesai")
        const dinasSelesaiIds = detailEvents
          .filter((de) => de.status === "selesai")
          .map((de) => de.dinas_id)
          .filter((id) => id !== null && id !== undefined);

        // Klasifikasi semua target dinas
        const allDinas = targetDinas.map((td) => {
          if (dinasSelesaiIds.includes(td.dinas_id)) {
            return { ...td, status: "selesai" };
          }
          return { ...td, status: "on_progress" };
        });

        // Urutkan: selesai di atas, on progress di bawah
        const sortedDinas = allDinas.sort((a, b) => {
          if (a.status === "selesai" && b.status === "on_progress") return -1;
          if (a.status === "on_progress" && b.status === "selesai") return 1;
          return 0;
        });

        setDinasSelesaiList(sortedDinas);
      } catch (error) {
        console.error("Error fetching dinas selesai:", error);
        swal({
          title: "Error",
          text: "Gagal mengambil data dinas selesai",
          icon: "error",
        });
        setDinasSelesaiList([]);
      } finally {
        setLoadingDinasSelesai(false);
      }
    },
    []
  );

  const handleCloseDinasSelesaiDialog = () => {
    setOpenDinasSelesaiDialog(false);
    setDinasSelesaiList([]);
    setSelectedEventIdForDinasSelesai(null);
    setEventDataForDialog(null);
  };

  // Generate PDF untuk dialog dinas selesai
  const generatePDFDinasSelesai = async (eventData, dinasList) => {
    const doc = new jsPDF();
    const logoBase64 = await loadLogoBase64();
    addLogoToPdf(doc, logoBase64);
    let yPosition = 20;

    // Header
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text("Laporan Status Selesai Event", 105, yPosition, {
      align: "center",
    });
    yPosition += 10;

    // Informasi Event
    doc.setFontSize(12);
    doc.setFont("helvetica", "normal");
    doc.text(`Nama Event: ${eventData?.kegiatan || "-"}`, 14, yPosition);
    yPosition += 7;

    // Tanggal cetak
    const tanggalCetak = new Date().toLocaleString("id-ID", {
      day: "numeric",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
    doc.text(`Dicetak: ${tanggalCetak}`, 14, yPosition);
    yPosition += 7;
    doc.text("Dicetak melalui: Aplikasi ESA Office", 14, yPosition);
    yPosition += 10;

    // Separator
    doc.line(14, yPosition, 196, yPosition);
    yPosition += 10;

    // Daftar Dinas
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text("Daftar Dinas Peserta", 14, yPosition);
    yPosition += 10;

    // Table header
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    const tableStartY = yPosition;
    doc.text("No", 14, yPosition);
    doc.text("Status", 30, yPosition);
    doc.text("Nama Dinas", 60, yPosition);
    yPosition += 6;

    // Table rows
    doc.setFont("helvetica", "normal");
    dinasList.forEach((dinas, index) => {
      // Check if need new page
      if (yPosition > 270) {
        doc.addPage();
        yPosition = 20;
        // Redraw header
        doc.setFont("helvetica", "bold");
        doc.text("No", 14, yPosition);
        doc.text("Status", 30, yPosition);
        doc.text("Nama Dinas", 60, yPosition);
        yPosition += 6;
        doc.setFont("helvetica", "normal");
      }

      const currentY = yPosition;
      doc.text(`${index + 1}`, 14, currentY);

      // Status
      doc.setFont("helvetica", "bold");
      const statusText = dinas.status === "selesai" ? "Selesai" : "On Progress";
      doc.text(statusText, 30, currentY);
      doc.setFont("helvetica", "normal");

      // Nama dinas - split jika panjang
      const namaDinas = dinas.nama_dinas || "-";
      const namaLines = doc.splitTextToSize(namaDinas, 120);
      const maxLines = namaLines.length;
      const lineHeight = 5;

      for (let i = 0; i < maxLines; i++) {
        const rowY = currentY + i * lineHeight;
        if (i === 0) {
          doc.text(namaLines[i] || "", 60, rowY);
        } else {
          doc.text(namaLines[i] || "", 60, rowY);
        }
      }

      yPosition = currentY + maxLines * lineHeight + 2;
    });

    // Draw table border
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.1);
    const tableEndY = yPosition - 2;
    doc.line(14, tableStartY - 4, 14, tableEndY); // Left border
    doc.line(196, tableStartY - 4, 196, tableEndY); // Right border
    doc.line(14, tableStartY - 4, 196, tableStartY - 4); // Top border
    doc.line(14, tableEndY, 196, tableEndY); // Bottom border
    doc.line(28, tableStartY - 4, 28, tableEndY); // No border
    doc.line(58, tableStartY - 4, 58, tableEndY); // Status border

    // Generate PDF blob
    const pdfBlob = doc.output("blob");
    const url = URL.createObjectURL(pdfBlob);
    return url;
  };

  // Generate PDF untuk dialog dinas hadir
  const generatePDFDinasHadir = async (eventData, dinasList) => {
    const doc = new jsPDF();
    const logoBase64 = await loadLogoBase64();
    addLogoToPdf(doc, logoBase64);
    let yPosition = 20;

    // Header
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text("Laporan Kehadiran Dinas Event", 105, yPosition, {
      align: "center",
    });
    yPosition += 10;

    // Informasi Event
    doc.setFontSize(12);
    doc.setFont("helvetica", "normal");
    doc.text(`Nama Event: ${eventData?.kegiatan || "-"}`, 14, yPosition);
    yPosition += 7;

    // Tanggal cetak
    const tanggalCetak = new Date().toLocaleString("id-ID", {
      day: "numeric",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
    doc.text(`Dicetak: ${tanggalCetak}`, 14, yPosition);
    yPosition += 7;
    doc.text("Dicetak melalui: Aplikasi ESA Office", 14, yPosition);
    yPosition += 10;

    // Separator
    doc.line(14, yPosition, 196, yPosition);
    yPosition += 10;

    // Daftar Dinas
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text("Daftar Dinas Peserta", 14, yPosition);
    yPosition += 10;

    // Table header
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    const tableStartY = yPosition;
    doc.text("No", 14, yPosition);
    doc.text("Status", 30, yPosition);
    doc.text("Nama Dinas", 60, yPosition);
    yPosition += 6;

    // Table rows
    doc.setFont("helvetica", "normal");
    dinasList.forEach((dinas, index) => {
      // Check if need new page
      if (yPosition > 270) {
        doc.addPage();
        yPosition = 20;
        // Redraw header
        doc.setFont("helvetica", "bold");
        doc.text("No", 14, yPosition);
        doc.text("Status", 30, yPosition);
        doc.text("Nama Dinas", 60, yPosition);
        yPosition += 6;
        doc.setFont("helvetica", "normal");
      }

      const currentY = yPosition;
      doc.text(`${index + 1}`, 14, currentY);

      // Status
      doc.setFont("helvetica", "bold");
      const statusText = dinas.status === "hadir" ? "Hadir" : "Belum Hadir";
      doc.text(statusText, 30, currentY);
      doc.setFont("helvetica", "normal");

      // Nama dinas - split jika panjang
      const namaDinas = dinas.nama_dinas || "-";
      const namaLines = doc.splitTextToSize(namaDinas, 120);
      const maxLines = namaLines.length;
      const lineHeight = 5;

      for (let i = 0; i < maxLines; i++) {
        const rowY = currentY + i * lineHeight;
        if (i === 0) {
          doc.text(namaLines[i] || "", 60, rowY);
        } else {
          doc.text(namaLines[i] || "", 60, rowY);
        }
      }

      yPosition = currentY + maxLines * lineHeight + 2;
    });

    // Draw table border
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.1);
    const tableEndY = yPosition - 2;
    doc.line(14, tableStartY - 4, 14, tableEndY); // Left border
    doc.line(196, tableStartY - 4, 196, tableEndY); // Right border
    doc.line(14, tableStartY - 4, 196, tableStartY - 4); // Top border
    doc.line(14, tableEndY, 196, tableEndY); // Bottom border
    doc.line(28, tableStartY - 4, 28, tableEndY); // No border
    doc.line(58, tableStartY - 4, 58, tableEndY); // Status border

    // Generate PDF blob
    const pdfBlob = doc.output("blob");
    const url = URL.createObjectURL(pdfBlob);
    return url;
  };

  const handleOpenDokumen = (dokumen) => {
    if (!dokumen) return;
    // Cek apakah dokumen adalah URL eksternal atau path upload
    const isExternal = /^https?:\/\//i.test(dokumen);
    if (isExternal) {
      // Buka URL eksternal di tab baru
      window.open(dokumen, "_blank");
    } else {
      // Buka file upload di tab baru
      const fullUrl = dokumen.startsWith("/")
        ? `${API_ESA_OFFICE_UPLOAD}${dokumen}`
        : `${API_ESA_OFFICE_UPLOAD}/${dokumen}`;
      window.open(fullUrl, "_blank");
    }
  };

  const handleOpenFotoDialog = (detailEvent) => {
    if (
      detailEvent.foto &&
      Array.isArray(detailEvent.foto) &&
      detailEvent.foto.length > 0
    ) {
      setViewingFotos(detailEvent.foto);
      setOpenFotoDialog(true);
    }
  };

  const handleCloseFotoDialog = () => {
    setOpenFotoDialog(false);
    setViewingFotos([]);
  };

  const handleOpenEditDetailDialog = async (detailEvent) => {
    console.log("handleOpenEditDetailDialog - detailEvent:", detailEvent);
    if (!detailEvent.id_events || !detailEvent.id) {
      console.error("Missing id_events or id in detailEvent:", detailEvent);
      swal({
        title: "Error",
        text: "Data detail event tidak valid. id_events atau id tidak ditemukan.",
        icon: "error",
      });
      return;
    }

    const isEvaluasiOrAsistensi =
      detailEvent.status_event === "evaluasi" ||
      detailEvent.status_event === "asistensi";

    // Fetch target dinas jika evaluasi/asistensi
    let targetDinas = [];
    if (isEvaluasiOrAsistensi) {
      try {
        const response = await axios.get(
          `${API_ESA_OFFICE}events/${detailEvent.id_events}/target-dinas`
        );
        targetDinas = (response.data || []).filter(
          (d) => d.dinas_id && d.nama_dinas
        );
      } catch (error) {
        console.error("Error fetching target dinas:", error);
        targetDinas = [];
      }
    }
    setTargetDinasForEdit(targetDinas);

    setEditingDetailEvent(detailEvent);
    setDetailFormData({
      detail_kegiatan: detailEvent.detail_kegiatan || "",
      waktu_mulai: detailEvent.waktu_mulai
        ? new Date(detailEvent.waktu_mulai).toISOString().slice(0, 16)
        : "",
      waktu_finish: detailEvent.waktu_finish
        ? new Date(detailEvent.waktu_finish).toISOString().slice(0, 16)
        : "",
      tempat: detailEvent.tempat || userNamaDinas || "",
      dinas_id: detailEvent.dinas_id || null,
    });
    setOpenEditDetailDialog(true);
  };

  const handleCloseEditDetailDialog = () => {
    setOpenEditDetailDialog(false);
    setEditingDetailEvent(null);
    setTargetDinasForEdit([]);
    setDetailFormData({
      detail_kegiatan: "",
      waktu_mulai: "",
      waktu_finish: "",
      tempat: "",
      dinas_id: null,
    });
    setSubmittingDetail(false);
  };

  const handleDetailFormChange = (field, value) => {
    setDetailFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmitEditDetail = async () => {
    if (submittingDetail || !editingDetailEvent) return;

    // Validasi
    if (!detailFormData.waktu_mulai) {
      swal({
        title: "Error",
        text: "Waktu mulai harus diisi",
        icon: "error",
      });
      return;
    }

    // Validasi id_events dan id
    if (!editingDetailEvent.id_events || !editingDetailEvent.id) {
      swal({
        title: "Error",
        text: "Data detail event tidak valid",
        icon: "error",
      });
      console.error("editingDetailEvent:", editingDetailEvent);
      return;
    }

    const isEvaluasiOrAsistensi =
      editingDetailEvent.status_event === "evaluasi" ||
      editingDetailEvent.status_event === "asistensi";

    // Validasi dinas_id untuk evaluasi/asistensi
    if (isEvaluasiOrAsistensi && !detailFormData.dinas_id) {
      swal({
        title: "Error",
        text: "Dinas peserta harus dipilih untuk event evaluasi atau asistensi",
        icon: "error",
      });
      return;
    }

    setSubmittingDetail(true);
    try {
      const payload = {
        detail_kegiatan: detailFormData.detail_kegiatan || null,
        waktu_mulai: detailFormData.waktu_mulai,
        waktu_finish: detailFormData.waktu_finish || null,
        tempat: detailFormData.tempat || null,
      };

      // Tambahkan dinas_id jika evaluasi/asistensi
      if (isEvaluasiOrAsistensi) {
        payload.dinas_id = detailFormData.dinas_id;
      }

      await axios.put(
        `${API_ESA_OFFICE}events/${editingDetailEvent.id_events}/detail/${editingDetailEvent.id}`,
        payload
      );

      swal({
        title: "Berhasil!",
        text: "Detail event berhasil diupdate",
        icon: "success",
        button: "OK",
      });

      handleCloseEditDetailDialog();
      fetchDetailEvents();
    } catch (error) {
      console.error("Error updating detail event:", error);
      swal({
        title: "Error!",
        text: error.response?.data?.message || "Gagal mengupdate detail event",
        icon: "error",
        button: "OK",
      });
    } finally {
      setSubmittingDetail(false);
    }
  };

  const handleDeleteDetailEvent = async (detailEvent) => {
    swal({
      title: "Apakah Anda yakin?",
      text: "Data detail event akan dihapus secara permanen!",
      icon: "warning",
      buttons: ["Batal", "Ya, Hapus!"],
      dangerMode: true,
    }).then(async (willDelete) => {
      if (willDelete) {
        try {
          await axios.delete(
            `${API_ESA_OFFICE}events/detail/${detailEvent.id}`
          );
          swal({
            title: "Berhasil!",
            text: "Detail event berhasil dihapus",
            icon: "success",
            button: "OK",
          });
          fetchDetailEvents();
        } catch (error) {
          swal({
            title: "Error!",
            text:
              error.response?.data?.message ||
              "Terjadi kesalahan saat menghapus detail event",
            icon: "error",
            button: "OK",
          });
        }
      }
    });
  };

  const handleDropzoneChange = (files) => {
    // Hitung total file (existing + baru)
    const totalFiles = existingFotos.length + (files ? files.length : 0);

    // Validasi maksimal 3 file total
    if (totalFiles > 3) {
      swal({
        title: "Error",
        text: "Maksimal 3 foto total (termasuk foto yang sudah ada)",
        icon: "error",
      });
      if (dropzoneRef.current && dropzoneRef.current.dropzone) {
        dropzoneRef.current.dropzone.removeAllFiles(true);
      }
      return;
    }

    // Validasi ukuran file (maksimal 1.5MB per file)
    if (files && files.length > 0) {
      const oversizedFiles = files.filter((file) => {
        const fileObj = file.file || file;
        return fileObj.size > 1.5 * 1024 * 1024;
      });

      if (oversizedFiles.length > 0) {
        swal({
          title: "Error",
          text: "Ukuran file melebihi 1.5 MB. Silakan pilih file yang lebih kecil",
          icon: "error",
        });
        if (dropzoneRef.current && dropzoneRef.current.dropzone) {
          dropzoneRef.current.dropzone.removeAllFiles(true);
        }
        return;
      }
    }

    setFormData((prev) => ({
      ...prev,
      foto: files || [],
    }));
  };

  const handleSubmit = async () => {
    if (submitting) return;

    setSubmitting(true);
    try {
      // 1. Upload foto jika ada
      let fotoUrls = [];
      if (formData.foto && formData.foto.length > 0) {
        const uploadPromises = formData.foto.map(async (file) => {
          const formDataUpload = new FormData();
          formDataUpload.append("file", file.file || file);
          const uploadResponse = await axios.post(
            `${API_ESA_OFFICE}upload`,
            formDataUpload,
            {
              headers: { "Content-Type": "multipart/form-data" },
            }
          );
          return (
            uploadResponse.data.filePath ||
            `/uploads/${uploadResponse.data.filename}`
          );
        });
        fotoUrls = await Promise.all(uploadPromises);
      }

      // 2. Merge foto existing (yang tidak dihapus) dengan foto baru
      const remainingExistingFotos = existingFotos.filter(
        (url) => !removedFotoUrls.includes(url)
      );
      let finalFotoUrls = [...remainingExistingFotos, ...fotoUrls];

      // Jika tidak ada foto sama sekali, gunakan null
      if (finalFotoUrls.length === 0) {
        finalFotoUrls = null;
      }

      // 3. Update detail event
      await axios.put(`${API_ESA_OFFICE}events/detail/${editingId}`, {
        notulen: formData.notulen || null,
        foto: finalFotoUrls,
      });

      swal({
        title: "Berhasil!",
        text: "Laporan event berhasil disimpan",
        icon: "success",
        button: "OK",
      });

      handleCloseDialog();
      fetchDetailEvents();
    } catch (error) {
      console.error("Error saving laporan:", error);
      swal({
        title: "Error!",
        text: error.response?.data?.message || "Gagal menyimpan laporan",
        icon: "error",
        button: "OK",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusEventLabel = (status) => {
    switch (status) {
      case "satu_kali":
        return "Satu Kali";
      case "rutin":
        return "Rutin";
      case "evaluasi":
        return "Evaluasi";
      case "asistensi":
        return "Asistensi";
      default:
        return status;
    }
  };

  const getStatusEventColor = (status) => {
    switch (status) {
      case "satu_kali":
        return "info";
      case "rutin":
        return "warning";
      case "evaluasi":
        return "success";
      case "asistensi":
        return "secondary";
      default:
        return "default";
    }
  };

  const getScopeLabel = (scope) => {
    if (!scope) return "-";
    const s = String(scope).toLowerCase();
    if (s === "internal") return "Internal";
    if (s === "pemda") return "Pemda";
    if (s === "publik") return "Publik";
    return scope;
  };

  const getScopeColor = (scope) => {
    if (!scope) return "default";
    const s = String(scope).toLowerCase();
    if (s === "internal") return "primary";
    if (s === "pemda") return "info";
    if (s === "publik") return "success";
    return "default";
  };

  // Kelompokkan detail events berdasarkan event
  const groupedEvents = useMemo(() => {
    const grouped = {};
    detailEventsList.forEach((detailEvent) => {
      const eventId = detailEvent.id_events;
      if (!grouped[eventId]) {
        grouped[eventId] = {
          id_events: eventId,
          kegiatan: detailEvent.kegiatan || "-",
          status_event: detailEvent.status_event || "-",
          scope: detailEvent.scope || null,
          nama_dinas: detailEvent.nama_dinas,
          nama_bidang: detailEvent.nama_bidang,
          // Gunakan data pembuat event, bukan pembuat detail event
          uid_RSS_pembuat: detailEvent.event_uid_RSS_pembuat || null,
          nama_pembuat: detailEvent.event_nama_pembuat || null,
          event_id_pembuat: detailEvent.event_id_pembuat || null,
          detailEvents: [],
        };
      }
      grouped[eventId].detailEvents.push(detailEvent);
    });

    // Hitung jumlah detail events yang sudah selesai dan field tambahan
    const groups = Object.values(grouped).map((group) => {
      const sorted = [...group.detailEvents].sort((a, b) => {
        const ta = a.waktu_mulai ? new Date(a.waktu_mulai).getTime() : 0;
        const tb = b.waktu_mulai ? new Date(b.waktu_mulai).getTime() : 0;
        return tb - ta;
      });
      const latestDetail = sorted[0];
      const latestWaktuMulai = latestDetail?.waktu_mulai
        ? new Date(latestDetail.waktu_mulai).getTime()
        : 0;
      const totalPeserta = group.detailEvents.reduce(
        (sum, de) => sum + (parseInt(de.jumlah_peserta, 10) || 0),
        0
      );
      const dinasPesertaSet = new Set();
      group.detailEvents.forEach((de) => {
        const nama = de.dinas_peserta_nama?.trim();
        if (nama) dinasPesertaSet.add(nama);
      });
      const dinasPesertaList = Array.from(dinasPesertaSet);
      const jumlahDetailEventsSelesai = group.detailEvents.filter(
        (de) => de.status === "selesai"
      ).length;

      return {
        ...group,
        jumlah_detail_events: group.detailEvents.length,
        jumlah_detail_events_selesai: jumlahDetailEventsSelesai,
        latestWaktuMulai,
        latestWaktuFormatted: formatDate(latestDetail?.waktu_mulai),
        latestTempat: latestDetail?.tempat || "-",
        totalPeserta,
        dinasPesertaList,
      };
    });

    // Urutkan dari terbaru ke terlama berdasarkan waktu_mulai terbaru
    return groups.sort((a, b) => b.latestWaktuMulai - a.latestWaktuMulai);
  }, [detailEventsList]);

  // Filter groupedEvents berdasarkan search query
  const filteredGroupedEvents = useMemo(() => {
    if (!searchQuery.trim()) {
      return groupedEvents;
    }
    const query = searchQuery.toLowerCase().trim();
    return groupedEvents.filter((group) =>
      group.kegiatan?.toLowerCase().includes(query)
    );
  }, [groupedEvents, searchQuery]);

  // Fetch statistics untuk setiap event
  useEffect(() => {
    const fetchStatistics = async () => {
      const stats = {};
      for (const group of groupedEvents) {
        try {
          const response = await axios.get(
            `${API_ESA_OFFICE}events/${group.id_events}/statistics`
          );
          stats[group.id_events] = response.data;
        } catch (error) {
          console.error(
            `Error fetching statistics for event ${group.id_events}:`,
            error
          );
          // Set default values jika error
          stats[group.id_events] = {
            total_peserta: 0,
            jumlah_dinas: 0,
            total_target_dinas: 0,
            jumlah_dinas_tanpa_event: 0,
            jumlah_dinas_sudah_hadir: 0,
            jumlah_dinas_belum_hadir: 0,
            progress_persentase: 0,
            dinas_sudah_hadir: [],
            dinas_belum_hadir: [],
            progress_md: "",
          };
        }
      }
      setEventStatistics(stats);
    };

    if (groupedEvents.length > 0) {
      fetchStatistics();
    }
  }, [groupedEvents]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <MDTypography variant="h6" color="white">
                  Laporan Event
                </MDTypography>
                <Tooltip title="Refresh Data">
                  <IconButton
                    color="white"
                    onClick={() => fetchDetailEvents()}
                    disabled={loading}
                  >
                    <RefreshIcon sx={{ color: "white" }} />
                  </IconButton>
                </Tooltip>
              </MDBox>
              <MDBox pt={4} pb={3} px={3}>
                {/* Filter Tahun dan Bulan */}
                <Grid container spacing={2} mb={3}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Autocomplete
                      options={(() => {
                        const currentYear = new Date().getFullYear();
                        const years = [];
                        // Mulai dari 2025 sampai current tahun
                        for (let i = 2025; i <= currentYear; i++) {
                          years.push({ label: i.toString(), value: i });
                        }
                        // Urutkan dari terbaru ke terlama (DESC)
                        return years.reverse();
                      })()}
                      value={
                        tahun ? { label: tahun.toString(), value: tahun } : null
                      }
                      onChange={(event, newValue) => {
                        if (newValue) {
                          setTahun(newValue.value);
                        }
                      }}
                      renderInput={(params) => (
                        <MDInput
                          {...params}
                          label="Tahun"
                          variant="standard"
                          fullWidth
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Autocomplete
                      options={[
                        { label: "Semua Bulan", value: "semua" },
                        { label: "Januari", value: 1 },
                        { label: "Februari", value: 2 },
                        { label: "Maret", value: 3 },
                        { label: "April", value: 4 },
                        { label: "Mei", value: 5 },
                        { label: "Juni", value: 6 },
                        { label: "Juli", value: 7 },
                        { label: "Agustus", value: 8 },
                        { label: "September", value: 9 },
                        { label: "Oktober", value: 10 },
                        { label: "November", value: 11 },
                        { label: "Desember", value: 12 },
                      ]}
                      value={
                        !bulan || bulan === "semua"
                          ? { label: "Semua Bulan", value: "semua" }
                          : {
                              label: [
                                "Januari",
                                "Februari",
                                "Maret",
                                "April",
                                "Mei",
                                "Juni",
                                "Juli",
                                "Agustus",
                                "September",
                                "Oktober",
                                "November",
                                "Desember",
                              ][bulan - 1],
                              value: bulan,
                            }
                      }
                      onChange={(event, newValue) => {
                        if (newValue) {
                          setBulan(
                            newValue.value === "semua" ? null : newValue.value
                          );
                        }
                      }}
                      renderInput={(params) => (
                        <MDInput
                          {...params}
                          label="Bulan"
                          variant="standard"
                          fullWidth
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={6}>
                    <MDInput
                      label="Cari Nama Event"
                      variant="standard"
                      fullWidth
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <SearchIcon sx={{ color: "text.secondary", mr: 1 }} />
                        ),
                      }}
                    />
                  </Grid>
                </Grid>
                {loading ? (
                  <MDBox
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    minHeight="200px"
                  >
                    <CircularProgress />
                    <MDTypography variant="body2" color="text" ml={2}>
                      Memuat data...
                    </MDTypography>
                  </MDBox>
                ) : filteredGroupedEvents.length === 0 ? (
                  <MDBox
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    py={4}
                  >
                    <MDTypography variant="h6" color="text.secondary">
                      {searchQuery.trim()
                        ? "Tidak ada event yang sesuai dengan pencarian"
                        : "Tidak ada data event"}
                    </MDTypography>
                    <MDTypography variant="body2" color="text.secondary">
                      {searchQuery.trim()
                        ? "Coba ubah kata kunci pencarian"
                        : "Belum ada event yang terdaftar"}
                    </MDTypography>
                  </MDBox>
                ) : (
                  <Grid container spacing={2}>
                    {filteredGroupedEvents.map((group) => (
                      <Grid item xs={12} sm={6} md={4} key={group.id_events}>
                        <Card
                          sx={{
                            p: 2,
                            boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                            transition: "box-shadow 0.2s, transform 0.2s",
                            "&:hover": {
                              boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
                              transform: "translateY(-1px)",
                            },
                          }}
                        >
                          <MDBox display="flex" flexDirection="column" gap={1.5}>
                            <MDTypography variant="body2" sx={{ lineHeight: 1.35, fontSize: "0.875rem", fontWeight: 400 }}>
                              {group.kegiatan}
                            </MDTypography>
                            <MDBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
                              <Chip
                                label={getStatusEventLabel(group.status_event)}
                                color={getStatusEventColor(group.status_event)}
                                size="small"
                                variant="filled"
                              />
                              {group.scope && (
                                <Chip
                                  label={getScopeLabel(group.scope)}
                                  color={getScopeColor(group.scope)}
                                  size="small"
                                  variant="outlined"
                                />
                              )}
                              <MDTypography variant="caption" color="text.secondary">
                                {group.jumlah_detail_events} sesi
                              </MDTypography>
                            </MDBox>
                            <MDBox display="flex" flexDirection="column" gap={0.5} sx={{ "& .MuiSvgIcon-root": { fontSize: 14, mr: 0.5, verticalAlign: "middle" } }}>
                              <MDBox display="flex" alignItems="center" gap={0.5}>
                                <CalendarTodayIcon sx={{ fontSize: 14, opacity: 0.7 }} />
                                <MDTypography variant="caption" color="text.secondary">
                                  Terbaru: {group.latestWaktuFormatted}
                                </MDTypography>
                              </MDBox>
                              <MDBox display="flex" alignItems="center" gap={0.5}>
                                <PlaceIcon sx={{ fontSize: 14, opacity: 0.7 }} />
                                <MDTypography variant="caption" color="text.secondary" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "100%" }}>
                                  Tempat: {group.latestTempat}
                                </MDTypography>
                              </MDBox>
                              {group.dinasPesertaList?.length > 0 && (
                                <MDBox display="flex" alignItems="flex-start" gap={0.5}>
                                  <BusinessIcon sx={{ fontSize: 14, opacity: 0.7, mt: 0.2 }} />
                                  <MDTypography variant="caption" color="text.secondary">
                                    Dinas: {group.dinasPesertaList.slice(0, 2).join(", ")}
                                    {group.dinasPesertaList.length > 2 && ` +${group.dinasPesertaList.length - 2}`}
                                  </MDTypography>
                                </MDBox>
                              )}
                              <MDBox display="flex" alignItems="center" gap={0.5}>
                                <PeopleIcon sx={{ fontSize: 14, opacity: 0.7 }} />
                                <MDTypography variant="caption" color="text.secondary">
                                  Total peserta: {eventStatistics[group.id_events]?.total_peserta ?? group.totalPeserta ?? 0}
                                </MDTypography>
                              </MDBox>
                            </MDBox>
                            <MDBox
                              display="flex"
                              alignItems="center"
                              gap={1}
                              p={1}
                              sx={{
                                borderRadius: 1,
                                bgcolor: "grey.50",
                                border: "1px solid",
                                borderColor: "divider",
                              }}
                            >
                              <Tooltip title="Klik untuk tampilkan QR Code diperbesar">
                                <MDBox
                                  onClick={() => {
                                    setQrDialogEvent(group);
                                    setOpenQrDialog(true);
                                  }}
                                  sx={{
                                    flexShrink: 0,
                                    p: 0.5,
                                    bgcolor: "white",
                                    borderRadius: 1,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    cursor: "pointer",
                                    transition: "box-shadow 0.2s",
                                    "&:hover": { boxShadow: 1 },
                                  }}
                                >
                                  <QRCodeSVG value={`${typeof window !== "undefined" ? window.location.origin : ""}/#/publik/events/daftar-hadir/${group.id_events}`} size={44} level="M" />
                                </MDBox>
                              </Tooltip>
                              <MDBox flex={1} minWidth={0} display="flex" flexDirection="column" gap={0.25}>
                                <MDTypography variant="caption" color="text.secondary" fontWeight="medium">
                                  Daftar Hadir
                                </MDTypography>
                                <MDBox display="flex" alignItems="center" gap={0.5}>
                                  <LinkIcon sx={{ fontSize: 14, color: "text.secondary", opacity: 0.8 }} />
                                  <MDTypography
                                    variant="caption"
                                    color="primary"
                                    sx={{
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                      cursor: "pointer",
                                      "&:hover": { textDecoration: "underline" },
                                    }}
                                    onClick={() => window.open(`${typeof window !== "undefined" ? window.location.origin : ""}/#/publik/events/daftar-hadir/${group.id_events}`, "_blank")}
                                  >
                                    Buka link
                                  </MDTypography>
                                </MDBox>
                              </MDBox>
                            </MDBox>
                            <MDBox display="flex" justifyContent="space-between" alignItems="center" gap={1} pt={1} sx={{ borderTop: 1, borderColor: "divider" }}>
                              <MDBox display="flex" alignItems="center" gap={0.75} sx={{ minWidth: 0 }}>
                                <MDBox position="relative" sx={{ width: 28, height: 28, flexShrink: 0 }}>
                                  <MDBox
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    sx={{
                                      position: "absolute",
                                      inset: 0,
                                      width: 28,
                                      height: 28,
                                      borderRadius: "50%",
                                      backgroundColor: "grey.200",
                                      color: "grey.500",
                                    }}
                                  >
                                    <PersonIcon sx={{ fontSize: 18 }} />
                                  </MDBox>
                                  {group.uid_RSS_pembuat && (
                                    <MDBox
                                      component="img"
                                      src={`https://smartdh.rotendaokab.go.id/uploads/${group.uid_RSS_pembuat}.jpg`}
                                      alt=""
                                      sx={{
                                        position: "absolute",
                                        inset: 0,
                                        width: 28,
                                        height: 28,
                                        borderRadius: "50%",
                                        objectFit: "cover",
                                      }}
                                      onError={(e) => { e.target.style.display = "none"; }}
                                    />
                                  )}
                                </MDBox>
                                <MDTypography variant="caption" color="text.secondary">
                                  {group.nama_pembuat || "-"}
                                </MDTypography>
                              </MDBox>
                              <Tooltip title="Lihat Detail Laporan">
                                <IconButton
                                  color="primary"
                                  size="small"
                                  onClick={() => navigate(`/events/laporan-event/detail/${group.id_events}`)}
                                  sx={{ bgcolor: "action.hover", flexShrink: 0 }}
                                >
                                  <VisibilityIcon />
                                </IconButton>
                              </Tooltip>
                            </MDBox>
                          </MDBox>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* Dialog Form Laporan */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <MDBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <MDTypography variant="h6">Laporan Event</MDTypography>
            <IconButton onClick={handleCloseDialog}>
              <CloseIcon />
            </IconButton>
          </MDBox>
        </DialogTitle>
        <DialogContent>
          <MDBox pt={2} pb={3} px={3}>
            <MDBox mb={3}>
              <MDBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                <MDTypography
                  component="label"
                  variant="button"
                  fontWeight="regular"
                  color="text"
                >
                  Notulen
                </MDTypography>
              </MDBox>
              <MDEditor
                value={formData.notulen || ""}
                onChange={(val) =>
                  setFormData((prev) => ({
                    ...prev,
                    notulen: val,
                  }))
                }
              />
            </MDBox>
            <MDBox>
              <MDBox mb={1}>
                <MDTypography
                  component="label"
                  variant="button"
                  fontWeight="regular"
                  color="text"
                >
                  Foto Kegiatan (Opsional)
                  <br />
                  <MDTypography
                    component="span"
                    variant="caption"
                    color="text.secondary"
                  >
                    Format: JPG, PNG | Maksimal: 3 file, 1.5 MB per file
                  </MDTypography>
                </MDTypography>
              </MDBox>

              {/* Preview Foto Existing */}
              {existingFotos.length > 0 && (
                <MDBox mb={3}>
                  <MDTypography
                    variant="caption"
                    fontWeight="medium"
                    color="text"
                    mb={1}
                  >
                    Foto yang sudah diupload:
                  </MDTypography>
                  <Grid container spacing={2}>
                    {existingFotos.map((fotoUrl, index) => (
                      <Grid item xs={6} sm={4} md={3} key={index}>
                        <Card
                          sx={{
                            position: "relative",
                            paddingTop: "100%",
                            overflow: "hidden",
                          }}
                        >
                          <img
                            src={
                              fotoUrl.startsWith("/")
                                ? `${API_ESA_OFFICE_UPLOAD}${fotoUrl}`
                                : `${API_ESA_OFFICE_UPLOAD}/${fotoUrl}`
                            }
                            alt={`Foto ${index + 1}`}
                            style={{
                              position: "absolute",
                              top: 0,
                              left: 0,
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                            }}
                            onError={(e) => {
                              e.target.src =
                                "https://via.placeholder.com/300?text=Gambar+Tidak+Tersedia";
                            }}
                          />
                          <IconButton
                            onClick={() => handleRemoveExistingFoto(fotoUrl)}
                            sx={{
                              position: "absolute",
                              top: 4,
                              right: 4,
                              backgroundColor: "rgba(255, 255, 255, 0.9)",
                              "&:hover": {
                                backgroundColor: "rgba(255, 255, 255, 1)",
                              },
                              color: "error.main",
                              padding: "4px",
                            }}
                            size="small"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </MDBox>
              )}

              {useMemo(
                () => (
                  <MDDropzone
                    options={{
                      addRemoveLinks: true,
                      uploadMultiple: true,
                      maxFiles: 3,
                      maxFilesize: 1.5,
                      acceptedFiles: ".jpg,.jpeg,.png",
                      dictDefaultMessage:
                        "Klik atau seret foto ke sini untuk mengunggah",
                      dictMaxFilesExceeded: "Maksimal 3 file",
                      dictFileTooBig:
                        "Ukuran file melebihi 1.5 MB. Silakan pilih file yang lebih kecil",
                      dictInvalidFileType:
                        "Format file tidak didukung. Hanya JPG dan PNG yang diperbolehkan",
                    }}
                    onChange={handleDropzoneChange}
                    ref={dropzoneRef}
                  />
                ),
                []
              )}
            </MDBox>
          </MDBox>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={submitting}>
            Batal
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="info"
            disabled={submitting}
            startIcon={
              submitting ? <CircularProgress size={16} color="inherit" /> : null
            }
          >
            {submitting ? "Menyimpan..." : "Simpan"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog View Notulen dan Foto - PDF */}
      <Dialog
        open={openViewDialog}
        onClose={handleCloseViewDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <MDBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <MDTypography variant="h6">Lihat Laporan Event (PDF)</MDTypography>
            <IconButton onClick={handleCloseViewDialog}>
              <CloseIcon />
            </IconButton>
          </MDBox>
        </DialogTitle>
        <DialogContent>
          <MDBox pt={2} pb={3} px={3}>
            {pdfBlobUrl ? (
              <iframe
                src={pdfBlobUrl}
                width="100%"
                height="600px"
                title="PDF Viewer"
                style={{ border: "none" }}
                onError={(e) => {
                  console.error("Error loading PDF:", e);
                  swal({
                    icon: "error",
                    title: "Oops...",
                    text: "Gagal memuat PDF",
                    button: "OK",
                  });
                }}
              />
            ) : (
              <MDBox
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight="200px"
              >
                <CircularProgress />
                <MDTypography variant="body2" color="text" ml={2}>
                  Membuat PDF...
                </MDTypography>
              </MDBox>
            )}
          </MDBox>
        </DialogContent>
        <DialogActions>
          {pdfBlobUrl && (
            <Button
              onClick={() => {
                const link = document.createElement("a");
                link.href = pdfBlobUrl;
                link.download = `Laporan_Event_${
                  viewingDetailEvent?.kegiatan || "Event"
                }_${new Date().getTime()}.pdf`;
                link.click();
              }}
              variant="outlined"
              sx={{
                color: "#000000 !important",
                borderColor: "#000000 !important",
                backgroundColor: "#ffffff",
                "&:hover": {
                  borderColor: "#000000 !important",
                  backgroundColor: "#f5f5f5",
                  color: "#000000 !important",
                },
              }}
            >
              Download PDF
            </Button>
          )}
          <Button
            onClick={handleCloseViewDialog}
            variant="contained"
            color="primary"
          >
            Tutup
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Progress MD */}
      <Dialog
        open={openProgressDialog}
        onClose={() => {
          setOpenProgressDialog(false);
          setViewingProgressMD("");
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <MDBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <MDTypography variant="h6">Progress Kehadiran Dinas</MDTypography>
            <IconButton
              onClick={() => {
                setOpenProgressDialog(false);
                setViewingProgressMD("");
              }}
            >
              <CloseIcon />
            </IconButton>
          </MDBox>
        </DialogTitle>
        <DialogContent>
          <MDBox pt={2} pb={3} px={3}>
            {viewingProgressMD ? (
              <MDBox
                sx={{
                  "& h2": {
                    fontSize: "1.5rem",
                    fontWeight: "bold",
                    marginTop: "1rem",
                    marginBottom: "0.5rem",
                  },
                  "& h3": {
                    fontSize: "1.25rem",
                    fontWeight: "bold",
                    marginTop: "1rem",
                    marginBottom: "0.5rem",
                  },
                  "& p": {
                    marginBottom: "0.5rem",
                  },
                  "& ul": {
                    marginLeft: "1.5rem",
                    marginBottom: "0.5rem",
                  },
                  "& li": {
                    marginBottom: "0.25rem",
                  },
                  "& strong": {
                    fontWeight: "bold",
                  },
                }}
              >
                <MDEditor
                  value={viewingProgressMD}
                  readOnly
                  preview="preview"
                />
              </MDBox>
            ) : (
              <MDTypography variant="body2" color="text.secondary">
                Tidak ada data progress
              </MDTypography>
            )}
          </MDBox>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenProgressDialog(false);
              setViewingProgressMD("");
            }}
            variant="contained"
            color="primary"
          >
            Tutup
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Daftar Peserta */}
      <Dialog
        open={openPesertaDialog}
        onClose={handleClosePesertaDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <MDBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <MDTypography variant="h6">Daftar Peserta</MDTypography>
            <MDBox display="flex" alignItems="center" gap={1}>
              <Button
                variant="outlined"
                size="small"
                startIcon={
                  downloadingPesertaPdf ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <PictureAsPdfIcon fontSize="small" />
                  )
                }
                onClick={handleDownloadPesertaPDF}
                disabled={!daftarPeserta?.length || downloadingPesertaPdf}
                sx={{
                  textTransform: "none",
                  color: "#000000 !important",
                  borderColor: "#000000 !important",
                  backgroundColor: "#ffffff",
                  "&:hover": {
                    borderColor: "#000000 !important",
                    backgroundColor: "#f5f5f5",
                    color: "#000000 !important",
                  },
                }}
              >
                Download
              </Button>
              <IconButton onClick={handleClosePesertaDialog} size="small">
                <CloseIcon />
              </IconButton>
            </MDBox>
          </MDBox>
        </DialogTitle>
        <DialogContent>
          <MDBox pt={2} pb={3} px={3}>
            {loadingPeserta ? (
              <MDBox
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight="200px"
              >
                <CircularProgress />
              </MDBox>
            ) : daftarPeserta.length === 0 ? (
              <MDBox
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight="200px"
              >
                <MDTypography variant="body2" color="text.secondary">
                  Tidak ada peserta
                </MDTypography>
              </MDBox>
            ) : (
              <MDBox>
                <DataTable
                  table={{
                    columns: [
                      { Header: "No", accessor: "no", width: "5%" },
                      {
                        Header: "Nama",
                        accessor: "nama",
                        width: "25%",
                        Cell: ({ row }) => (
                          <MDBox display="flex" alignItems="center" gap={1}>
                            {row.original.uid_RSS ? (
                              <img
                                src={`https://smartdh.rotendaokab.go.id/uploads/${row.original.uid_RSS}.jpg`}
                                alt={
                                  row.original.nama_user || row.original.nama
                                }
                                style={{
                                  width: 32,
                                  height: 32,
                                  borderRadius: "50%",
                                  objectFit: "cover",
                                }}
                                onError={(e) => {
                                  e.target.style.display = "none";
                                }}
                              />
                            ) : (
                              <MDBox
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                sx={{
                                  width: 32,
                                  height: 32,
                                  borderRadius: "50%",
                                  backgroundColor: "grey.200",
                                  color: "grey.500",
                                }}
                              >
                                <PersonIcon sx={{ fontSize: 20 }} />
                              </MDBox>
                            )}
                            <MDTypography variant="caption" color="text">
                              {row.original.nama_user ||
                                row.original.nama ||
                                "-"}
                            </MDTypography>
                          </MDBox>
                        ),
                      },
                      {
                        Header: "Instansi",
                        accessor: "instansi",
                        width: "200px",
                        Cell: ({ value }) => (
                          <MDBox
                            sx={{
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              width: "200px",
                              py: 1,
                            }}
                          >
                            <MDTypography variant="caption" color="text">
                              {value || "-"}
                            </MDTypography>
                          </MDBox>
                        ),
                      },
                      {
                        Header: "Waktu",
                        accessor: "waktu",
                        width: "18%",
                        Cell: ({ value }) => (
                          <MDTypography variant="caption" color="text">
                            {value ? formatDateTime(value) : "-"}
                          </MDTypography>
                        ),
                      },
                      {
                        Header: "Tanda Tangan",
                        accessor: "signature_file",
                        width: "15%",
                        Cell: ({ row }) => {
                          const sig = row.original.signature_file;
                          if (!sig) return <MDTypography variant="caption" color="text.secondary">-</MDTypography>;
                          const imgUrl = `${API_ESA_OFFICE_UPLOAD}/uploads/${sig}`;
                          return (
                            <MDBox display="flex" alignItems="center" minHeight={70}>
                              <MDBox
                                component="img"
                                src={imgUrl}
                                alt="Tanda tangan"
                                sx={{
                                  maxWidth: 140,
                                  maxHeight: 70,
                                  objectFit: "contain",
                                }}
                                onError={(e) => {
                                  e.target.style.display = "none";
                                  const fallback = e.target.nextElementSibling;
                                  if (fallback) fallback.style.display = "inline";
                                }}
                              />
                              <MDTypography variant="caption" color="text.secondary" sx={{ display: "none" }}>
                                -
                              </MDTypography>
                            </MDBox>
                          );
                        },
                      },
                    ],
                    rows: daftarPeserta.map((peserta, index) => ({
                      no: index + 1,
                      nama: peserta.nama_user || peserta.nama,
                      instansi: peserta.instansi,
                      waktu: peserta.waktu,
                      uid_RSS: peserta.uid_RSS,
                      nama_user: peserta.nama_user,
                      signature_file: peserta.signature_file,
                    })),
                  }}
                  isSorted={false}
                  entriesPerPage={false}
                  showTotalEntries={false}
                  noEndBorder
                />
              </MDBox>
            )}
          </MDBox>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleClosePesertaDialog}
            variant="contained"
            color="primary"
          >
            Tutup
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Edit Detail Event */}
      <Dialog
        open={openEditDetailDialog}
        onClose={handleCloseEditDetailDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <MDBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <MDTypography variant="h6">Edit Detail Event</MDTypography>
            <IconButton onClick={handleCloseEditDetailDialog}>
              <CloseIcon />
            </IconButton>
          </MDBox>
        </DialogTitle>
        <DialogContent>
          <MDBox pt={2} pb={3} px={3}>
            <MDBox mb={3}>
              <MDBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                <MDTypography
                  component="label"
                  variant="button"
                  fontWeight="regular"
                  color="text"
                >
                  Detail Kegiatan
                </MDTypography>
              </MDBox>
              <MDInput
                type="text"
                fullWidth
                value={detailFormData.detail_kegiatan}
                onChange={(e) =>
                  handleDetailFormChange("detail_kegiatan", e.target.value)
                }
                placeholder="Masukkan detail kegiatan"
              />
            </MDBox>

            <MDBox mb={3}>
              <MDBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                <MDTypography
                  component="label"
                  variant="button"
                  fontWeight="regular"
                  color="text"
                >
                  Waktu Mulai <span style={{ color: "red" }}>*</span>
                </MDTypography>
              </MDBox>
              <MDInput
                type="datetime-local"
                fullWidth
                value={detailFormData.waktu_mulai}
                onChange={(e) =>
                  handleDetailFormChange("waktu_mulai", e.target.value)
                }
                required
              />
            </MDBox>

            <MDBox mb={3}>
              <MDBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                <MDTypography
                  component="label"
                  variant="button"
                  fontWeight="regular"
                  color="text"
                >
                  Waktu Selesai
                </MDTypography>
              </MDBox>
              <MDInput
                type="datetime-local"
                fullWidth
                value={detailFormData.waktu_finish}
                onChange={(e) =>
                  handleDetailFormChange("waktu_finish", e.target.value)
                }
              />
            </MDBox>

            {/* Pilih Dinas Peserta - hanya untuk evaluasi/asistensi */}
            {editingDetailEvent &&
              (editingDetailEvent.status_event === "evaluasi" ||
                editingDetailEvent.status_event === "asistensi") && (
                <MDBox mb={3}>
                  <MDBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                    <MDTypography
                      component="label"
                      variant="button"
                      fontWeight="regular"
                      color="text"
                    >
                      Dinas Peserta <span style={{ color: "red" }}>*</span>
                    </MDTypography>
                  </MDBox>
                  <Autocomplete
                    options={targetDinasForEdit.filter(
                      (d) => d.dinas_id && d.nama_dinas
                    )}
                    value={
                      targetDinasForEdit.find(
                        (d) => d.dinas_id === detailFormData.dinas_id
                      ) || null
                    }
                    onChange={(event, newValue) =>
                      handleDetailFormChange(
                        "dinas_id",
                        newValue ? newValue.dinas_id : null
                      )
                    }
                    getOptionLabel={(option) => option.nama_dinas || ""}
                    isOptionEqualToValue={(option, value) =>
                      option.dinas_id === value?.dinas_id
                    }
                    renderInput={(params) => (
                      <MDInput
                        {...params}
                        placeholder="Pilih dinas peserta"
                        fullWidth
                        required
                      />
                    )}
                    renderOption={(props, option) => (
                      <li {...props} key={option.dinas_id}>
                        {truncateDinasName(option.nama_dinas)}
                      </li>
                    )}
                    disabled={targetDinasForEdit.length === 0}
                    noOptionsText="Tidak ada dinas target untuk event ini"
                  />
                </MDBox>
              )}
            <MDBox mb={3}>
              <MDBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                <MDTypography
                  component="label"
                  variant="button"
                  fontWeight="regular"
                  color="text"
                >
                  Tempat/Lokasi
                </MDTypography>
              </MDBox>
              <Autocomplete
                freeSolo
                options={dinasList}
                value={detailFormData.tempat}
                onChange={(event, newValue) => {
                  handleDetailFormChange(
                    "tempat",
                    newValue
                      ? typeof newValue === "string"
                        ? newValue
                        : newValue.label || newValue
                      : ""
                  );
                }}
                onInputChange={(event, newInputValue) => {
                  handleDetailFormChange("tempat", newInputValue);
                }}
                renderInput={(params) => (
                  <MDInput
                    {...params}
                    placeholder="Masukkan atau pilih tempat"
                    fullWidth
                  />
                )}
              />
            </MDBox>
          </MDBox>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseEditDetailDialog}
            disabled={submittingDetail}
          >
            Batal
          </Button>
          <Button
            onClick={handleSubmitEditDetail}
            variant="contained"
            color="info"
            disabled={submittingDetail}
            startIcon={
              submittingDetail ? (
                <CircularProgress size={16} color="inherit" />
              ) : null
            }
          >
            {submittingDetail ? "Menyimpan..." : "Simpan"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Dinas Tanpa Event */}
      <Dialog
        open={openDinasTanpaEventDialog}
        onClose={handleCloseDinasTanpaEventDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <MDBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <MDTypography variant="h6">Dinas Tanpa Event</MDTypography>
            <IconButton onClick={handleCloseDinasTanpaEventDialog}>
              <CloseIcon />
            </IconButton>
          </MDBox>
        </DialogTitle>
        <DialogContent>
          <MDBox pt={2} pb={3} px={3}>
            {loadingDinasTanpaEvent ? (
              <MDBox
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight="200px"
              >
                <CircularProgress />
              </MDBox>
            ) : dinasTanpaEvent.length === 0 ? (
              <MDBox
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight="200px"
              >
                <MDTypography variant="body2" color="text.secondary">
                  Semua dinas target sudah memiliki detail event
                </MDTypography>
              </MDBox>
            ) : (
              <MDBox>
                <DataTable
                  table={{
                    columns: [
                      { Header: "No", accessor: "no", width: "10%" },
                      {
                        Header: "Nama Dinas",
                        accessor: "nama_dinas",
                        width: "90%",
                        Cell: ({ value }) => (
                          <MDBox
                            sx={{
                              whiteSpace: "normal",
                              wordWrap: "break-word",
                              py: 1,
                              width: "300px",
                              maxHeight: "3em",
                              overflow: "hidden",
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                            }}
                          >
                            <MDTypography variant="body2" color="text">
                              {value || "-"}
                            </MDTypography>
                          </MDBox>
                        ),
                      },
                    ],
                    rows: dinasTanpaEvent.map((dinas, index) => ({
                      no: index + 1,
                      nama_dinas: dinas.nama_dinas || "-",
                      dinas_id: dinas.dinas_id,
                    })),
                  }}
                  isSorted={false}
                  entriesPerPage={false}
                  showTotalEntries={false}
                  noEndBorder
                />
              </MDBox>
            )}
          </MDBox>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseDinasTanpaEventDialog}
            variant="contained"
            color="primary"
          >
            Tutup
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Dinas Hadir/Belum Hadir */}
      <Dialog
        open={openDinasHadirDialog}
        onClose={handleCloseDinasHadirDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <MDBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <MDTypography variant="h6">Dinas Hadir / Belum Hadir</MDTypography>
            <IconButton onClick={handleCloseDinasHadirDialog}>
              <CloseIcon />
            </IconButton>
          </MDBox>
        </DialogTitle>
        <DialogContent>
          <MDBox pt={2} pb={3} px={3}>
            {loadingDinasHadir ? (
              <MDBox
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight="200px"
              >
                <CircularProgress />
              </MDBox>
            ) : dinasHadirList.length === 0 ? (
              <MDBox
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight="200px"
              >
                <MDTypography variant="body2" color="text.secondary">
                  Tidak ada data dinas
                </MDTypography>
              </MDBox>
            ) : (
              <MDBox>
                <DataTable
                  table={{
                    columns: [
                      { Header: "No", accessor: "no", width: "5%" },
                      {
                        Header: "Status",
                        accessor: "status",
                        width: "10%",
                        Cell: ({ row }) => (
                          <MDBox display="flex" justifyContent="center">
                            {row.original.status === "hadir" ? (
                              <CheckCircleIcon
                                sx={{ color: "success.main", fontSize: 24 }}
                              />
                            ) : (
                              <CancelIcon
                                sx={{ color: "error.main", fontSize: 24 }}
                              />
                            )}
                          </MDBox>
                        ),
                      },
                      {
                        Header: "Nama Dinas",
                        accessor: "nama_dinas",
                        width: "85%",
                        Cell: ({ value }) => (
                          <MDBox
                            sx={{
                              whiteSpace: "normal",
                              wordWrap: "break-word",
                              py: 1,
                              width: "400px",
                              maxHeight: "3em",
                              overflow: "hidden",
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                            }}
                          >
                            <MDTypography variant="body2" color="text">
                              {value || "-"}
                            </MDTypography>
                          </MDBox>
                        ),
                      },
                    ],
                    rows: dinasHadirList.map((dinas, index) => ({
                      no: index + 1,
                      nama_dinas: dinas.nama_dinas || "-",
                      dinas_id: dinas.dinas_id,
                      status: dinas.status,
                    })),
                  }}
                  isSorted={false}
                  entriesPerPage={false}
                  showTotalEntries={false}
                  noEndBorder
                />
              </MDBox>
            )}
          </MDBox>
        </DialogContent>
        <DialogActions>
          {dinasHadirList.length > 0 && eventDataForDinasHadirDialog && (
            <Button
              onClick={async () => {
                const pdfUrl = await generatePDFDinasHadir(
                  eventDataForDinasHadirDialog,
                  dinasHadirList
                );
                const link = document.createElement("a");
                link.href = pdfUrl;
                link.download = `Laporan_Kehadiran_Dinas_${
                  eventDataForDinasHadirDialog?.kegiatan || "Event"
                }_${new Date().getTime()}.pdf`;
                link.click();
                // Clean up blob URL after a delay
                setTimeout(() => {
                  URL.revokeObjectURL(pdfUrl);
                }, 100);
              }}
              variant="contained"
              color="info"
              startIcon={<PictureAsPdfIcon />}
              sx={{ color: "black", borderColor: "black" }}
            >
              Download PDF
            </Button>
          )}
          <Button
            onClick={handleCloseDinasHadirDialog}
            variant="contained"
            color="primary"
            sx={{ color: "white", borderColor: "white" }}
          >
            Tutup
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Dinas Sudah Selesai */}
      <Dialog
        open={openDinasSelesaiDialog}
        onClose={handleCloseDinasSelesaiDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <MDBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <MDTypography variant="h6">
              Dinas Sudah Selesai / On Progress
            </MDTypography>
            <IconButton onClick={handleCloseDinasSelesaiDialog}>
              <CloseIcon />
            </IconButton>
          </MDBox>
        </DialogTitle>
        <DialogContent>
          <MDBox pt={2} pb={3} px={3}>
            {loadingDinasSelesai ? (
              <MDBox
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight="200px"
              >
                <CircularProgress />
              </MDBox>
            ) : dinasSelesaiList.length === 0 ? (
              <MDBox
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight="200px"
              >
                <MDTypography variant="body2" color="text.secondary">
                  Tidak ada data dinas
                </MDTypography>
              </MDBox>
            ) : (
              <MDBox>
                <DataTable
                  table={{
                    columns: [
                      { Header: "No", accessor: "no", width: "5%" },
                      {
                        Header: "Status",
                        accessor: "status",
                        width: "15%",
                        Cell: ({ row }) => (
                          <MDBox
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                            gap={1}
                          >
                            {row.original.status === "selesai" ? (
                              <>
                                <CheckCircleIcon
                                  sx={{ color: "success.main", fontSize: 24 }}
                                />
                                <MDTypography
                                  variant="caption"
                                  color="success.main"
                                  fontWeight="bold"
                                >
                                  Selesai
                                </MDTypography>
                              </>
                            ) : (
                              <>
                                <AccessTimeIcon
                                  sx={{ color: "warning.main", fontSize: 24 }}
                                />
                                <MDTypography
                                  variant="caption"
                                  color="warning.main"
                                  fontWeight="bold"
                                >
                                  On Progress
                                </MDTypography>
                              </>
                            )}
                          </MDBox>
                        ),
                      },
                      {
                        Header: "Nama Dinas",
                        accessor: "nama_dinas",
                        width: "80%",
                        Cell: ({ value }) => (
                          <MDBox
                            sx={{
                              whiteSpace: "normal",
                              wordWrap: "break-word",
                              py: 1,
                              width: "400px",
                              maxHeight: "3em",
                              overflow: "hidden",
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                            }}
                          >
                            <MDTypography variant="body2" color="text">
                              {value || "-"}
                            </MDTypography>
                          </MDBox>
                        ),
                      },
                    ],
                    rows: dinasSelesaiList.map((dinas, index) => ({
                      no: index + 1,
                      nama_dinas: dinas.nama_dinas || "-",
                      dinas_id: dinas.dinas_id,
                      status: dinas.status,
                    })),
                  }}
                  isSorted={false}
                  entriesPerPage={false}
                  showTotalEntries={false}
                  noEndBorder
                />
              </MDBox>
            )}
          </MDBox>
        </DialogContent>
        <DialogActions>
          {dinasSelesaiList.length > 0 && eventDataForDialog && (
            <Button
              onClick={async () => {
                const pdfUrl = await generatePDFDinasSelesai(
                  eventDataForDialog,
                  dinasSelesaiList
                );
                const link = document.createElement("a");
                link.href = pdfUrl;
                link.download = `Laporan_Status_Selesai_${
                  eventDataForDialog?.kegiatan || "Event"
                }_${new Date().getTime()}.pdf`;
                link.click();
                // Clean up blob URL after a delay
                setTimeout(() => {
                  URL.revokeObjectURL(pdfUrl);
                }, 100);
              }}
              variant="outlined"
              color="info"
              startIcon={<PictureAsPdfIcon />}
              sx={{ color: "black", borderColor: "black" }}
            >
              Download PDF
            </Button>
          )}
          <Button
            onClick={handleCloseDinasSelesaiDialog}
            variant="contained"
            color="primary"
          >
            Tutup
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Foto Kegiatan */}
      <Dialog
        open={openFotoDialog}
        onClose={handleCloseFotoDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <MDBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <MDTypography variant="h6">Foto Kegiatan</MDTypography>
            <IconButton onClick={handleCloseFotoDialog}>
              <CloseIcon />
            </IconButton>
          </MDBox>
        </DialogTitle>
        <DialogContent>
          <MDBox pt={2} pb={3} px={3}>
            {viewingFotos.length === 0 ? (
              <MDTypography variant="body2" color="text.secondary">
                Tidak ada foto
              </MDTypography>
            ) : (
              <Grid container spacing={2}>
                {viewingFotos.map((fotoUrl, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <Card>
                      <img
                        src={
                          fotoUrl.startsWith("/")
                            ? `${API_ESA_OFFICE_UPLOAD}${fotoUrl}`
                            : `${API_ESA_OFFICE_UPLOAD}/${fotoUrl}`
                        }
                        alt={`Foto ${index + 1}`}
                        style={{
                          width: "100%",
                          height: "auto",
                          display: "block",
                        }}
                        onError={(e) => {
                          e.target.src =
                            "https://via.placeholder.com/300?text=Gambar+Tidak+Tersedia";
                        }}
                      />
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </MDBox>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseFotoDialog}
            variant="contained"
            color="primary"
          >
            Tutup
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog QR Code Daftar Hadir */}
      <Dialog
        open={openQrDialog}
        onClose={() => {
          setOpenQrDialog(false);
          setQrDialogEvent(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <MDBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <MDTypography variant="h6">Daftar Hadir – QR Code</MDTypography>
            <IconButton
              onClick={() => {
                setOpenQrDialog(false);
                setQrDialogEvent(null);
              }}
            >
              <CloseIcon />
            </IconButton>
          </MDBox>
        </DialogTitle>
        <DialogContent>
          {qrDialogEvent && (
            <MDBox pt={1} pb={3} px={3}>
              <MDBox mb={3}>
                <MDTypography variant="body2" color="text" sx={{ mb: 1, fontWeight: 500 }}>
                  {qrDialogEvent.kegiatan}
                </MDTypography>
                <MDBox display="flex" alignItems="center" gap={1} flexWrap="wrap" mb={0.5}>
                  <Chip
                    label={getStatusEventLabel(qrDialogEvent.status_event)}
                    color={getStatusEventColor(qrDialogEvent.status_event)}
                    size="small"
                    variant="filled"
                  />
                  {qrDialogEvent.scope && (
                    <Chip
                      label={getScopeLabel(qrDialogEvent.scope)}
                      color={getScopeColor(qrDialogEvent.scope)}
                      size="small"
                      variant="outlined"
                    />
                  )}
                  <MDTypography variant="caption" color="text.secondary">
                    {qrDialogEvent.jumlah_detail_events} sesi
                  </MDTypography>
                </MDBox>
                {qrDialogEvent.latestTempat && (
                  <MDBox display="flex" alignItems="center" gap={0.5} flexWrap="wrap">
                    <PlaceIcon sx={{ fontSize: 14, opacity: 0.7 }} />
                    <MDTypography variant="caption" color="text.secondary">
                      {qrDialogEvent.latestTempat}
                    </MDTypography>
                  </MDBox>
                )}
              </MDBox>
              <MDBox
                display="flex"
                flexDirection="column"
                alignItems="center"
                gap={2}
                p={2}
                sx={{
                  borderRadius: 2,
                  bgcolor: "grey.50",
                  border: "1px solid",
                  borderColor: "divider",
                }}
              >
                <MDTypography variant="caption" color="text.secondary">
                  Scan QR Code berikut untuk mengisi daftar hadir
                </MDTypography>
                <MDBox
                  sx={{
                    p: 2,
                    bgcolor: "white",
                    borderRadius: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: 1,
                  }}
                >
                  <QRCodeSVG
                    value={`${typeof window !== "undefined" ? window.location.origin : ""}/#/publik/events/daftar-hadir/${qrDialogEvent.id_events}`}
                    size={220}
                    level="M"
                  />
                </MDBox>
                <MDBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
                  <LinkIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                  <MDTypography
                    variant="caption"
                    color="primary"
                    sx={{
                      cursor: "pointer",
                      "&:hover": { textDecoration: "underline" },
                    }}
                    onClick={() =>
                      window.open(
                        `${typeof window !== "undefined" ? window.location.origin : ""}/#/publik/events/daftar-hadir/${qrDialogEvent.id_events}`,
                        "_blank"
                      )
                    }
                  >
                    Buka link daftar hadir
                  </MDTypography>
                </MDBox>
              </MDBox>
            </MDBox>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenQrDialog(false);
              setQrDialogEvent(null);
            }}
            variant="contained"
            color="primary"
          >
            Tutup
          </Button>
        </DialogActions>
      </Dialog>

      <Footer />
    </DashboardLayout>
  );
}

export default LaporanEvent;
